import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { APP_RELEASE } from '../lib/app-release';

type WebReleaseManifest = {
  release?: unknown;
  builtAt?: unknown;
};

const WEB_RELEASE_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const WEB_RELEASE_RECHECK_MIN_INTERVAL_MS = 30 * 1000;
const UNKNOWN_RELEASES = new Set(['', 'unknown', 'local']);

function normalizeRelease(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || UNKNOWN_RELEASES.has(trimmed)) return null;
  return trimmed;
}

function canCheckWebRelease(currentRelease: string | null): boolean {
  return Platform.OS === 'web' && !__DEV__ && Boolean(currentRelease);
}

export function useWebReleaseUpdate() {
  const currentRelease = useMemo(() => normalizeRelease(APP_RELEASE), []);
  const [availableRelease, setAvailableRelease] = useState<string | null>(null);
  const [dismissedRelease, setDismissedRelease] = useState<string | null>(null);
  const checkInFlightRef = useRef(false);
  const lastCheckedAtRef = useRef(0);

  const checkForWebRelease = useCallback(
    async (force = false) => {
      if (!canCheckWebRelease(currentRelease)) return;
      if (checkInFlightRef.current) return;

      const now = Date.now();
      if (
        !force &&
        now - lastCheckedAtRef.current < WEB_RELEASE_RECHECK_MIN_INTERVAL_MS
      ) {
        return;
      }

      checkInFlightRef.current = true;
      lastCheckedAtRef.current = now;

      try {
        const response = await fetch(`/version.json?t=${now}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;

        const manifest = (await response.json()) as WebReleaseManifest;
        const nextRelease = normalizeRelease(manifest.release);
        if (!nextRelease || nextRelease === currentRelease) {
          setAvailableRelease(null);
          return;
        }

        setAvailableRelease(nextRelease);
      } catch {
        // Version checks should never interrupt the running app.
      } finally {
        checkInFlightRef.current = false;
      }
    },
    [currentRelease],
  );

  useEffect(() => {
    if (!canCheckWebRelease(currentRelease)) return;

    void checkForWebRelease(true);
    const intervalId = setInterval(() => {
      void checkForWebRelease();
    }, WEB_RELEASE_CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [checkForWebRelease, currentRelease]);

  useEffect(() => {
    if (!canCheckWebRelease(currentRelease)) return;
    if (typeof document === 'undefined') return;

    const checkWhenVisible = () => {
      if (document.visibilityState !== 'visible') return;
      void checkForWebRelease();
    };

    document.addEventListener('visibilitychange', checkWhenVisible);
    return () => {
      document.removeEventListener('visibilitychange', checkWhenVisible);
    };
  }, [checkForWebRelease, currentRelease]);

  const refreshWebApp = useCallback(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    // A distinct URL avoids revalidating a cached document from the old release.
    const url = new URL(window.location.href);
    url.searchParams.set('_release', availableRelease ?? String(Date.now()));
    window.location.replace(url.toString());
  }, [availableRelease]);

  const dismissWebUpdate = useCallback(() => {
    setDismissedRelease(availableRelease);
  }, [availableRelease]);

  return {
    isWebUpdateAvailable: Boolean(
      availableRelease && availableRelease !== dismissedRelease,
    ),
    refreshWebApp,
    dismissWebUpdate,
  };
}
